import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { motion } from 'framer-motion';
import {
  ChevronRight, Wrench, Clock, Shield, Phone, MessageCircle,
  ArrowRight, CheckCircle2, AlertCircle, Star
} from 'lucide-react';
import { Button } from '../components/ui/button';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Brand Page - Shows all models for a brand
export const BrandPage = () => {
  const { brandSlug } = useParams();
  const [brandData, setBrandData] = useState(null);
  const [models, setModels] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchBrandData();
  }, [brandSlug]);

  const fetchBrandData = async () => {
    try {
      const res = await fetch(`${API_URL}/api/seo/brands/${brandSlug}`);
      if (res.ok) {
        const data = await res.json();
        setBrandData(data.brand);
        setModels(data.models || []);
      }
    } catch (error) {
      console.error('Error fetching brand:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-[#0047FF]"></div>
      </div>
    );
  }

  if (!brandData) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center">
        <h1 className="text-2xl font-bold mb-4">Marca no encontrada</h1>
        <Link to="/" className="text-[#0047FF]">Volver al inicio</Link>
      </div>
    );
  }

  // Group models by category
  const modelsByCategory = models.reduce((acc, model) => {
    const cat = model.category;
    if (!acc[cat]) acc[cat] = [];
    acc[cat].push(model);
    return acc;
  }, {});

  const categoryNames = {
    'consolas': 'Consolas',
    'mandos': 'Mandos',
    'moviles': 'Móviles',
    'tablets': 'Tablets',
    'robots': 'Robots Aspiradores',
    'patinetes': 'Patinetes Eléctricos',
    'aspiradores': 'Aspiradores',
    'electrodomesticos': 'Electrodomésticos',
    'accesorios': 'Accesorios'
  };

  return (
    <>
      <Helmet>
        <title>{brandData.meta_title}</title>
        <meta name="description" content={brandData.meta_description} />
        <link rel="canonical" href={`https://rma.arreglatelo.es/reparacion/${brandSlug}`} />
      </Helmet>

      <div className="min-h-screen bg-white" data-testid="seo-brand-page">
        {/* Header */}
        <nav className="fixed top-0 left-0 right-0 z-50 bg-white border-b border-gray-100">
          <div className="max-w-7xl mx-auto px-4 py-4 flex items-center justify-between">
            <Link to="/" className="flex items-center gap-2">
              <div className="w-8 h-8 bg-[#0047FF] rounded-md flex items-center justify-center">
                <span className="text-white font-bold text-lg">A</span>
              </div>
              <span className="font-bold text-xl">Arreglatelo</span>
            </Link>
            
            <div className="flex items-center gap-4">
              <a href="tel:+34624473293" className="hidden md:flex items-center gap-2 text-sm text-gray-600">
                <Phone className="w-4 h-4" /> 624 47 32 93
              </a>
              <Link to="/register">
                <Button className="bg-[#0047FF] hover:bg-[#003BCC]">Solicitar Reparación</Button>
              </Link>
            </div>
          </div>
        </nav>

        {/* Hero */}
        <section className="pt-24 pb-12 px-4 bg-gradient-to-br from-[#0047FF]/5 to-white">
          <div className="max-w-7xl mx-auto">
            {/* Breadcrumbs */}
            <div className="flex items-center gap-2 text-sm text-gray-500 mb-6">
              <Link to="/" className="hover:text-[#0047FF]">Inicio</Link>
              <ChevronRight className="w-4 h-4" />
              <Link to="/reparaciones" className="hover:text-[#0047FF]">Reparaciones</Link>
              <ChevronRight className="w-4 h-4" />
              <span className="text-gray-900">{brandData.name}</span>
            </div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
            >
              <h1 className="text-4xl md:text-5xl font-bold mb-4" data-testid="brand-h1">
                Reparación {brandData.name}
              </h1>
              <p className="text-lg text-gray-600 max-w-3xl mb-6">
                {brandData.description}
              </p>
              
              {/* Stats */}
              <div className="flex flex-wrap gap-6">
                <div className="flex items-center gap-2">
                  <Shield className="w-5 h-5 text-[#0047FF]" />
                  <span className="text-sm">90 días garantía</span>
                </div>
                <div className="flex items-center gap-2">
                  <Clock className="w-5 h-5 text-[#0047FF]" />
                  <span className="text-sm">3-10 días</span>
                </div>
                <div className="flex items-center gap-2">
                  <Star className="w-5 h-5 text-yellow-500" />
                  <span className="text-sm">4.8/5 valoración</span>
                </div>
              </div>
            </motion.div>
          </div>
        </section>

        {/* Models by Category */}
        {Object.entries(modelsByCategory).map(([category, categoryModels]) => (
          <section key={category} className="py-12 px-4 border-b border-gray-100">
            <div className="max-w-7xl mx-auto">
              <h2 className="text-2xl font-bold mb-6">
                {categoryNames[category] || category}
              </h2>
              
              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
                {categoryModels.map((model, i) => (
                  <Link
                    key={i}
                    to={`/reparacion/${brandSlug}/${model.slug}`}
                    className="bg-white p-6 rounded-xl border border-gray-200 hover:border-[#0047FF] hover:shadow-lg transition-all group"
                    data-testid={`model-card-${model.slug}`}
                  >
                    <h3 className="font-semibold text-lg mb-2 group-hover:text-[#0047FF] transition-colors">
                      {model.name}
                    </h3>
                    
                    <div className="flex items-center gap-4 text-sm mb-4">
                      <span className="text-[#0047FF] font-medium">{model.repair_cost_range}</span>
                      <span className="text-gray-400">|</span>
                      <span className="text-gray-500">{model.repair_time}</span>
                    </div>
                    
                    {/* Common problems */}
                    <div className="space-y-1">
                      {model.common_problems?.slice(0, 3).map((problem, j) => (
                        <div key={j} className="flex items-center gap-2 text-sm text-gray-600">
                          <CheckCircle2 className="w-4 h-4 text-green-500" />
                          <span>{problem}</span>
                        </div>
                      ))}
                    </div>
                    
                    <div className="mt-4 flex items-center text-[#0047FF] text-sm font-medium">
                      Ver detalles <ArrowRight className="ml-1 w-4 h-4" />
                    </div>
                  </Link>
                ))}
              </div>
            </div>
          </section>
        ))}

        {/* CTA */}
        <section className="py-16 px-4 bg-[#0A0A0A] text-white">
          <div className="max-w-4xl mx-auto text-center">
            <h2 className="text-3xl font-bold mb-4">
              ¿Necesitas reparar tu {brandData.name}?
            </h2>
            <p className="text-gray-400 mb-8">
              Envío gratuito, 90 días de garantía y presupuesto sin compromiso.
            </p>
            <div className="flex flex-wrap justify-center gap-4">
              <Link to="/register">
                <Button size="lg" className="bg-[#E5FF00] hover:bg-[#d4ee00] text-black">
                  Solicitar Reparación <ArrowRight className="ml-2 w-5 h-5" />
                </Button>
              </Link>
              <a href="https://wa.me/34624473293">
                <Button size="lg" variant="outline" className="border-white text-white hover:bg-white hover:text-black">
                  <MessageCircle className="mr-2 w-5 h-5" /> WhatsApp
                </Button>
              </a>
            </div>
          </div>
        </section>

        {/* Footer */}
        <footer className="py-8 px-4 border-t border-gray-200">
          <div className="max-w-7xl mx-auto text-center text-sm text-gray-500">
            © {new Date().getFullYear()} Arreglatelo. Servicio técnico especializado en {brandData.name}.
          </div>
        </footer>
      </div>
    </>
  );
};

// Model Page - Shows specific device model details
export const ModelPage = () => {
  const { brandSlug, modelSlug } = useParams();
  const [modelData, setModelData] = useState(null);
  const [brandData, setBrandData] = useState(null);
  const [relatedModels, setRelatedModels] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchModelData();
  }, [brandSlug, modelSlug]);

  const fetchModelData = async () => {
    try {
      const res = await fetch(`${API_URL}/api/seo/models/${modelSlug}`);
      if (res.ok) {
        const data = await res.json();
        setModelData(data.model);
        setBrandData(data.brand);
        setRelatedModels(data.related_models || []);
      }
    } catch (error) {
      console.error('Error fetching model:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-[#0047FF]"></div>
      </div>
    );
  }

  if (!modelData) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center">
        <h1 className="text-2xl font-bold mb-4">Modelo no encontrado</h1>
        <Link to="/" className="text-[#0047FF]">Volver al inicio</Link>
      </div>
    );
  }

  return (
    <>
      <Helmet>
        <title>{modelData.meta_title}</title>
        <meta name="description" content={modelData.meta_description} />
        <link rel="canonical" href={`https://rma.arreglatelo.es/reparacion/${brandSlug}/${modelSlug}`} />
        
        {/* FAQ Schema */}
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "FAQPage",
            "mainEntity": [
              {
                "@type": "Question",
                "name": `¿Cuánto cuesta reparar ${modelData.name}?`,
                "acceptedAnswer": {
                  "@type": "Answer",
                  "text": `El coste de reparación de ${modelData.name} oscila entre ${modelData.repair_cost_range}.`
                }
              },
              {
                "@type": "Question",
                "name": `¿Cuánto tarda la reparación?`,
                "acceptedAnswer": {
                  "@type": "Answer",
                  "text": `El tiempo estimado es de ${modelData.repair_time}.`
                }
              }
            ]
          })}
        </script>
      </Helmet>

      <div className="min-h-screen bg-white" data-testid="seo-model-page">
        {/* Header */}
        <nav className="fixed top-0 left-0 right-0 z-50 bg-white border-b border-gray-100">
          <div className="max-w-7xl mx-auto px-4 py-4 flex items-center justify-between">
            <Link to="/" className="flex items-center gap-2">
              <div className="w-8 h-8 bg-[#0047FF] rounded-md flex items-center justify-center">
                <span className="text-white font-bold text-lg">A</span>
              </div>
              <span className="font-bold text-xl">Arreglatelo</span>
            </Link>
            
            <div className="flex items-center gap-4">
              <a href="tel:+34624473293" className="hidden md:flex items-center gap-2 text-sm text-gray-600">
                <Phone className="w-4 h-4" /> 624 47 32 93
              </a>
              <Link to="/register">
                <Button className="bg-[#0047FF] hover:bg-[#003BCC]">Solicitar Reparación</Button>
              </Link>
            </div>
          </div>
        </nav>

        {/* Hero */}
        <section className="pt-24 pb-12 px-4 bg-gradient-to-br from-[#0047FF]/5 to-white">
          <div className="max-w-7xl mx-auto">
            {/* Breadcrumbs */}
            <div className="flex items-center gap-2 text-sm text-gray-500 mb-6 flex-wrap">
              <Link to="/" className="hover:text-[#0047FF]">Inicio</Link>
              <ChevronRight className="w-4 h-4" />
              <Link to={`/reparacion/${brandSlug}`} className="hover:text-[#0047FF]">
                {brandData?.name || brandSlug}
              </Link>
              <ChevronRight className="w-4 h-4" />
              <span className="text-gray-900">{modelData.name}</span>
            </div>

            <div className="grid lg:grid-cols-2 gap-12">
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
              >
                <h1 className="text-4xl md:text-5xl font-bold mb-4" data-testid="model-h1">
                  Reparación {modelData.name}
                </h1>
                <p className="text-lg text-gray-600 mb-6">
                  {modelData.description}
                </p>

                {/* Price & Time */}
                <div className="flex flex-wrap gap-6 mb-8">
                  <div className="bg-[#0047FF]/10 px-6 py-4 rounded-xl">
                    <p className="text-sm text-gray-500">Precio desde</p>
                    <p className="text-2xl font-bold text-[#0047FF]">{modelData.repair_cost_range}</p>
                  </div>
                  <div className="bg-green-50 px-6 py-4 rounded-xl">
                    <p className="text-sm text-gray-500">Tiempo estimado</p>
                    <p className="text-2xl font-bold text-green-600">{modelData.repair_time}</p>
                  </div>
                </div>

                {/* CTA Buttons */}
                <div className="flex flex-wrap gap-4">
                  <Link to="/register">
                    <Button size="lg" className="bg-[#0047FF] hover:bg-[#003BCC]">
                      Solicitar Reparación <ArrowRight className="ml-2 w-5 h-5" />
                    </Button>
                  </Link>
                  <a href="https://wa.me/34624473293">
                    <Button size="lg" variant="outline">
                      <MessageCircle className="mr-2 w-5 h-5" /> WhatsApp
                    </Button>
                  </a>
                </div>
              </motion.div>

              {/* Common Problems */}
              <motion.div
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                className="bg-white rounded-2xl border border-gray-200 p-6"
              >
                <h2 className="text-xl font-bold mb-4 flex items-center gap-2">
                  <AlertCircle className="w-6 h-6 text-orange-500" />
                  Problemas Comunes
                </h2>
                <div className="space-y-3">
                  {modelData.common_problems?.map((problem, i) => (
                    <div key={i} className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg">
                      <CheckCircle2 className="w-5 h-5 text-green-500 flex-shrink-0" />
                      <span>{problem}</span>
                    </div>
                  ))}
                </div>
                
                <div className="mt-6 p-4 bg-green-50 rounded-lg">
                  <p className="text-sm text-green-800">
                    <strong>¿Tu problema está en la lista?</strong> Lo reparamos con 90 días de garantía.
                  </p>
                </div>
              </motion.div>
            </div>
          </div>
        </section>

        {/* How it Works */}
        <section className="py-16 px-4 bg-gray-50">
          <div className="max-w-7xl mx-auto">
            <h2 className="text-2xl font-bold mb-8 text-center">Cómo Funciona</h2>
            <div className="grid md:grid-cols-4 gap-6">
              {[
                { step: 1, title: 'Solicita', desc: 'Rellena el formulario con los datos de tu dispositivo' },
                { step: 2, title: 'Envía', desc: 'Recibe la etiqueta de envío gratuita por email' },
                { step: 3, title: 'Reparamos', desc: 'Nuestros técnicos reparan tu dispositivo' },
                { step: 4, title: 'Recibe', desc: 'Te lo devolvemos a casa reparado y garantizado' }
              ].map((item, i) => (
                <div key={i} className="text-center">
                  <div className="w-12 h-12 bg-[#0047FF] text-white rounded-full flex items-center justify-center text-xl font-bold mx-auto mb-4">
                    {item.step}
                  </div>
                  <h3 className="font-semibold mb-2">{item.title}</h3>
                  <p className="text-sm text-gray-600">{item.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Related Models */}
        {relatedModels.length > 0 && (
          <section className="py-16 px-4">
            <div className="max-w-7xl mx-auto">
              <h2 className="text-2xl font-bold mb-8">
                Otros modelos de {brandData?.name}
              </h2>
              <div className="grid md:grid-cols-3 lg:grid-cols-5 gap-4">
                {relatedModels.map((model, i) => (
                  <Link
                    key={i}
                    to={`/reparacion/${brandSlug}/${model.slug}`}
                    className="p-4 bg-white rounded-lg border border-gray-200 hover:border-[#0047FF] hover:shadow-md transition-all text-center"
                  >
                    <h3 className="font-medium text-sm mb-1">{model.name}</h3>
                    <p className="text-xs text-[#0047FF]">{model.repair_cost_range}</p>
                  </Link>
                ))}
              </div>
            </div>
          </section>
        )}

        {/* FAQ Section */}
        <section className="py-16 px-4 bg-gray-50">
          <div className="max-w-3xl mx-auto">
            <h2 className="text-2xl font-bold mb-8 text-center">Preguntas Frecuentes</h2>
            <div className="space-y-4">
              <div className="bg-white p-6 rounded-xl border border-gray-200">
                <h3 className="font-semibold mb-2">¿Cuánto cuesta reparar {modelData.name}?</h3>
                <p className="text-gray-600">
                  El coste de reparación oscila entre {modelData.repair_cost_range} dependiendo del tipo de avería.
                  Te damos un presupuesto exacto sin compromiso una vez recibamos el dispositivo.
                </p>
              </div>
              <div className="bg-white p-6 rounded-xl border border-gray-200">
                <h3 className="font-semibold mb-2">¿Cuánto tarda la reparación?</h3>
                <p className="text-gray-600">
                  El tiempo estimado es de {modelData.repair_time}. Esto incluye la recepción, diagnóstico, 
                  reparación y envío de vuelta a tu domicilio.
                </p>
              </div>
              <div className="bg-white p-6 rounded-xl border border-gray-200">
                <h3 className="font-semibold mb-2">¿Qué garantía tiene la reparación?</h3>
                <p className="text-gray-600">
                  Todas nuestras reparaciones incluyen 90 días de garantía. Si el problema persiste, 
                  lo reparamos de nuevo sin coste adicional.
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* CTA */}
        <section className="py-16 px-4 bg-[#0A0A0A] text-white">
          <div className="max-w-4xl mx-auto text-center">
            <h2 className="text-3xl font-bold mb-4">
              Repara tu {modelData.name} hoy
            </h2>
            <p className="text-gray-400 mb-8">
              Envío gratuito, diagnóstico sin compromiso y 90 días de garantía.
            </p>
            <Link to="/register">
              <Button size="lg" className="bg-[#E5FF00] hover:bg-[#d4ee00] text-black">
                Solicitar Reparación <ArrowRight className="ml-2 w-5 h-5" />
              </Button>
            </Link>
          </div>
        </section>

        {/* Footer */}
        <footer className="py-8 px-4 border-t border-gray-200">
          <div className="max-w-7xl mx-auto text-center text-sm text-gray-500">
            © {new Date().getFullYear()} Arreglatelo. Reparación {modelData.name} en toda España.
          </div>
        </footer>
      </div>
    </>
  );
};

export default ModelPage;
